import { useMutation, useQueryClient } from '@tanstack/react-query';
import { EditorState } from 'draft-js';

import { Button } from '@components/button';
import { IconButton } from '@components/icon-button';
import { IconCloseRegular } from '@components/icons/close-regular';
import { Separator } from '@components/separator';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { TextEditor, useEditorState } from '@components/text-editor';
import { useToaster } from '@components/toaster';
import { editorStateToHtml } from '@utils/draftjs';

import { Modal, ModalProps } from 'features/modals';
import { onboardingFormSetSms, SubmitError } from 'integrations/onboardingForm/api/requests';
import { useStore } from 'models/Provider';

type Props = ModalProps<{
  setEnabled: (enabled: boolean) => void;
  onEdit: boolean;
  template: string;
}>;

export const OnboardingSmsMessageModal = (props: Props) => {
  const { closeModal, setEnabled, onEdit, template } = props;
  const { t } = useStore();
  const { showSuccess, showError } = useToaster();
  const { editorState, resetEditorState, setEditorState, isEmpty } = useEditorState(
    onEdit ? template : t('integrations.onboardingform.sms.modal.initialtext'),
  );

  const handleClick = () => {
    const htmlText = editorStateToHtml(editorState);
    if (isEmpty) {
      showError(t('integrations.onboardingform.settings.sms.template.empty'));
    } else {
      submit.mutate(htmlText);
    }
  };

  const queryClient = useQueryClient();
  const submit = useMutation((template: string) => onboardingFormSetSms(template, true), {
    onSuccess: () => {
      queryClient.invalidateQueries(['smsData']);
      queryClient.invalidateQueries(['onboarding-form-data']);
      setEnabled(true);
      showSuccess(t('integrations.onboardingform.sms.modal.save.toast'));
      queryClient.refetchQueries(['onboarding-form-data']);
      closeModal();
    },
    onError: (error: SubmitError) => {
      showError(error.response ? error.response.data.error : error.message);
    },
  });

  const onChangeInput = (editorState: EditorState) => {
    setEditorState(editorState);
  };

  let count = editorState.getCurrentContent().getPlainText().length;
  let canSave = count <= 320 && !isEmpty;
  if (onEdit && editorStateToHtml(editorState) === template) {
    canSave = false;
  }

  return (
    <Modal>
      <Stack space={4}>
        <Stack orientation="horizontal" space={2} justify="space-between">
          <Text
            font="heading1"
            color="purple100"
            text={t('integrations.onboardingform.settings.sms')}
          />
          <IconButton variant="ghost-purple" onClick={closeModal} icon={<IconCloseRegular />} />
        </Stack>
        <Text font="regular16" text={t('integrations.onboardingform.sms.modal.subheader')} />
        <Separator />
        <Stack space={2}>
          <Text font="semibold14" text={t('integrations.onboardingform.sms.modal.label')} />
          <TextEditor editorState={editorState} onChange={onChangeInput} variant="gray">
            <TextEditor.Input collapseMargin stripPastedStyles disableShortcuts />
          </TextEditor>
          <Text
            font="regular14"
            color="purple80"
            text={count + t('integrations.onboardingform.settings.sms.template.characters')}
          />
          {count > 320 && (
            <Text
              font="regular14"
              color="red100"
              text={t('integrations.onboardingform.sms.modal.messagelong')}
            />
          )}
        </Stack>
        <Modal.Actions>
          <Button
            variant="secondary"
            onClick={() => {
              closeModal();
              resetEditorState();
            }}
            text={t('integrations.onboardingform.sms.modal.cancel')}
          />
          <Button
            variant="primary-blue"
            isDisabled={!canSave}
            text={t('integrations.onboardingform.sms.modal.save')}
            onClick={handleClick}
          />
        </Modal.Actions>
      </Stack>
    </Modal>
  );
};
